import React, { useCallback, useEffect, useMemo, useState } from "react";
import { CalendarClock, Calendar as CalendarIcon, Check, Pill, RotateCcw, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/consult/api";
import { Calendar } from "@/components/ui/calendar";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const GREEN = "#19734a";
const TEAL = "#2f7d64";
const RED = "#b84a4a";
const MUTED = "#687385";
const BORDER = "#e3e6e1";
const NAVY = "#1f3b2c";

const CARD = {
  background: "#fff",
  border: `1px solid ${BORDER}`,
  borderRadius: 12,
  boxShadow: "0 1px 2px rgba(16,24,40,.05)",
};

function toYMD(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function fromYMD(ymd) {
  return new Date(ymd + "T12:00:00");
}

function dayLabel(ymd, today, tomorrow) {
  if (ymd === today) return "Today";
  if (ymd === tomorrow) return "Tomorrow";
  return fromYMD(ymd).toLocaleDateString("en-CA", { weekday: "short", month: "short", day: "numeric", year: "numeric" });
}

function prettyYMD(ymd) {
  if (!ymd) return "No date";
  return fromYMD(ymd).toLocaleDateString("en-CA", { month: "short", day: "numeric", year: "numeric" });
}

function monthLabel(ym) {
  return fromYMD(ym + "-01").toLocaleDateString("en-CA", { month: "long", year: "numeric" });
}

export default function PharmacyPanel({ refreshKey = 0, onEdit }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  // View toggle — pending refills vs the permanent dispensed history.
  const [view, setView] = useState("pending");
  // Day / month filters — pending sorts by refill date, history by dispensed date.
  const [filterMonth, setFilterMonth] = useState("all");
  const [filterDay, setFilterDay] = useState("");
  const [dayPickerOpen, setDayPickerOpen] = useState(false);
  // Next-refill scheduling — offered right after a dispense is completed.
  const [nextRefillFor, setNextRefillFor] = useState(null);
  const [nextDate, setNextDate] = useState(null);
  const [savingNext, setSavingNext] = useState(false);
  // Cancel a scheduled refill (pending only) — confirmation required.
  const [cancelFor, setCancelFor] = useState(null);
  const [cancelBusy, setCancelBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/refills");
      setItems(Array.isArray(data) ? data : []);
    } catch {
      toast.error("Could not load refills.");
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load, refreshKey]);

  const setStatus = async (rec, status) => {
    setBusyId(rec.id);
    try {
      await api.patch(`/refills/${rec.id}`, { status });
      if (status === "dispensed") {
        // Offer to schedule the next refill right away — default 4 weeks
        // after the refill that was just dispensed (the program's cycle).
        const base = rec.refill_date ? fromYMD(rec.refill_date) : new Date();
        setNextDate(new Date(base.getTime() + 28 * 86400000));
        setNextRefillFor(rec);
      } else if (status === "pending") {
        toast.success(`${rec.patient_name} moved back to pending.`);
      }
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update the refill.");
    } finally {
      setBusyId(null);
    }
  };

  const scheduleNextRefill = async () => {
    if (!nextRefillFor || !nextDate) return;
    setSavingNext(true);
    try {
      // A brand-new ledger record — the dispensed one stays in the history.
      await api.post("/refills", {
        appointment_id: nextRefillFor.appointment_id,
        refill_date: toYMD(nextDate),
      });
      toast.success(`Next refill for ${nextRefillFor.patient_name} set to ${prettyYMD(toYMD(nextDate))}.`);
      setNextRefillFor(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not schedule the next refill.");
    } finally {
      setSavingNext(false);
    }
  };

  const cancelRefill = async () => {
    if (!cancelFor) return;
    setCancelBusy(true);
    try {
      // Status stays stored as "cancelled" — a trail, not a deletion.
      await api.patch(`/refills/${cancelFor.id}`, { status: "cancelled" });
      toast.success(`Refill cancelled for ${cancelFor.patient_name}.`);
      setCancelFor(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not cancel the refill.");
    } finally {
      setCancelBusy(false);
    }
  };

  const openEdit = async (rec) => {
    if (!onEdit) return;
    try {
      const { data } = await api.get(`/appointments/${rec.appointment_id}`);
      onEdit(data);
    } catch {
      toast.error("Could not open the client record.");
    }
  };

  const matchesFilters = (ymd) => {
    if (filterDay) return ymd === filterDay;
    if (filterMonth !== "all") return (ymd || "").startsWith(filterMonth);
    return true;
  };

  const { groups, history, monthOptions } = useMemo(() => {
    const pending = items
      .filter((r) => r.status === "pending" && matchesFilters(r.refill_date || ""))
      .sort((a, b) => (a.refill_date || "").localeCompare(b.refill_date || ""));
    const done = items
      .filter((r) => r.status === "dispensed" && matchesFilters((r.dispensed_at || "").slice(0, 10) || r.refill_date || ""))
      .sort((a, b) => ((b.dispensed_at || "").slice(0, 10) || b.refill_date || "")
        .localeCompare((a.dispensed_at || "").slice(0, 10) || a.refill_date || ""));
    const g = [];
    for (const r of pending) {
      const key = r.refill_date || "";
      const last = g[g.length - 1];
      if (last && last.key === key) last.rows.push(r);
      else g.push({ key, rows: [r] });
    }
    // Month dropdown options = every month present in the data (either date).
    const months = new Set();
    for (const r of items) {
      if (r.refill_date) months.add(r.refill_date.slice(0, 7));
      const d = (r.dispensed_at || "").slice(0, 10);
      if (d) months.add(d.slice(0, 7));
    }
    return { groups: g, history: done, monthOptions: [...months].sort().reverse() };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, filterMonth, filterDay]);

  const today = toYMD(new Date());
  const tomorrow = toYMD(new Date(Date.now() + 86400000));
  const pendingCount = groups.reduce((n, g) => n + g.rows.length, 0);

  const filterBtn = {
    display: "inline-flex", alignItems: "center", gap: 6,
    border: `1px solid ${BORDER}`, background: "#fff", color: NAVY,
    borderRadius: 8, padding: "7px 10px", fontSize: 12, fontWeight: 600,
    cursor: "pointer",
  };

  const cycleBadge = (r) => (
    <span
      style={{
        fontSize: 10, fontWeight: 800, color: TEAL,
        background: "#eef6f2", borderRadius: 6, padding: "2px 6px",
        letterSpacing: ".04em",
      }}
    >
      Refill #{r.cycle || 1}
    </span>
  );

  const renderPendingRow = (r) => {
    const overdue = r.refill_date && r.refill_date < today;
    return (
      <div
        key={r.id}
        data-testid={`pharmacy-refill-row-${r.id}`}
        style={{
          display: "flex", alignItems: "center", gap: 10,
          padding: "10px 12px",
          borderTop: `1px solid ${BORDER}`,
          flexWrap: "wrap",
        }}
      >
        <div style={{ minWidth: 0, flex: "1 1 180px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6, fontWeight: 700, color: NAVY, fontSize: 14 }}>
            <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.patient_name}</span>
            {cycleBadge(r)}
          </div>
          <div style={{ fontSize: 12, color: MUTED, marginTop: 1 }}>
            {r.medication || "No medication on file"}
            {overdue && <span style={{ color: RED, fontWeight: 700 }}> — overdue</span>}
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, fontWeight: 600, color: overdue ? RED : TEAL }}>
          <CalendarClock style={{ width: 14, height: 14 }} strokeWidth={2} />
          {prettyYMD(r.refill_date)}
        </div>
        <button
          type="button"
          disabled={busyId === r.id}
          onClick={() => setStatus(r, "dispensed")}
          data-testid={`pharmacy-refill-dispense-${r.id}`}
          style={{
            display: "inline-flex", alignItems: "center", gap: 5,
            border: "none", background: GREEN, color: "#fff",
            borderRadius: 8, padding: "7px 12px", fontSize: 12, fontWeight: 700,
            cursor: "pointer",
          }}
        >
          <Check style={{ width: 13, height: 13 }} strokeWidth={2.5} />
          Dispensed
        </button>
        {onEdit && (
          <button
            type="button"
            onClick={() => openEdit(r)}
            data-testid={`pharmacy-refill-edit-${r.id}`}
            style={{
              border: "none", background: "none", color: TEAL,
              fontSize: 12, fontWeight: 600, cursor: "pointer", textDecoration: "underline",
              padding: "4px 2px",
            }}
          >
            Edit
          </button>
        )}
        <button
          type="button"
          onClick={() => setCancelFor(r)}
          data-testid={`pharmacy-refill-cancel-${r.id}`}
          style={{
            border: "none", background: "none", color: RED,
            fontSize: 12, fontWeight: 600, cursor: "pointer",
            padding: "4px 2px",
          }}
        >
          Cancel
        </button>
      </div>
    );
  };

  const renderHistoryRow = (r) => (
    <div
      key={r.id}
      data-testid={`pharmacy-refill-row-${r.id}`}
      style={{
        display: "flex", alignItems: "center", gap: 10,
        padding: "10px 12px",
        borderTop: `1px solid ${BORDER}`,
        flexWrap: "wrap",
      }}
    >
      <div style={{ minWidth: 0, flex: "1 1 180px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6, fontWeight: 700, color: NAVY, fontSize: 14 }}>
          <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.patient_name}</span>
          {cycleBadge(r)}
        </div>
        <div style={{ fontSize: 12, color: MUTED, marginTop: 1 }}>
          {r.medication || "No medication on file"} — refill of {prettyYMD(r.refill_date)}
        </div>
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, fontWeight: 600, color: GREEN }}>
        <Check style={{ width: 14, height: 14 }} strokeWidth={2.5} />
        Dispensed {prettyYMD((r.dispensed_at || "").slice(0, 10))}
      </div>
      <span style={{ fontSize: 11, color: MUTED }}>by {r.dispensed_by || "staff"}</span>
      <button
        type="button"
        disabled={busyId === r.id}
        onClick={() => setStatus(r, "pending")}
        data-testid={`pharmacy-refill-undo-${r.id}`}
        style={{ ...filterBtn, color: MUTED }}
      >
        <RotateCcw style={{ width: 13, height: 13 }} strokeWidth={2} />
        Undo
      </button>
    </div>
  );

  return (
    <div data-testid="pharmacy-panel" style={{ display: "grid", gap: 14 }}>
      {/* View + day/month filters — pending by refill date, history by dispensed date */}
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
        <Select value={view} onValueChange={setView}>
          <SelectTrigger data-testid="pharmacy-view-select" className="h-9 bg-white text-xs" style={{ width: 150 }}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="pending" data-testid="pharmacy-view-pending">Pending</SelectItem>
            <SelectItem value="dispensed" data-testid="pharmacy-view-dispensed">Dispensed</SelectItem>
          </SelectContent>
        </Select>
        <Select value={filterMonth} onValueChange={setFilterMonth}>
          <SelectTrigger data-testid="pharmacy-filter-month" className="h-9 bg-white text-xs" style={{ width: 170 }}>
            <SelectValue placeholder="All months" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all" data-testid="pharmacy-filter-month-all">All months</SelectItem>
            {monthOptions.map((ym) => (
              <SelectItem key={ym} value={ym} data-testid={`pharmacy-filter-month-${ym}`}>
                {monthLabel(ym)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Popover open={dayPickerOpen} onOpenChange={setDayPickerOpen}>
          <PopoverTrigger asChild>
            <button type="button" data-testid="pharmacy-filter-day" style={filterBtn}>
              <CalendarIcon style={{ width: 14, height: 14, color: TEAL }} strokeWidth={2} />
              {filterDay ? prettyYMD(filterDay) : "Pick a day"}
            </button>
          </PopoverTrigger>
          <PopoverContent align="start" className="p-0 w-auto">
            <Calendar
              mode="single"
              selected={filterDay ? fromYMD(filterDay) : undefined}
              onSelect={(d) => {
                if (d) {
                  setFilterDay(toYMD(d));
                  setDayPickerOpen(false);
                }
              }}
              className="p-2"
            />
          </PopoverContent>
        </Popover>
        {(filterDay || filterMonth !== "all") && (
          <button
            type="button"
            data-testid="pharmacy-filter-clear"
            onClick={() => { setFilterDay(""); setFilterMonth("all"); }}
            style={{ ...filterBtn, color: MUTED }}
          >
            <X style={{ width: 13, height: 13 }} strokeWidth={2} />
            Clear
          </button>
        )}
      </div>

      {view === "pending" ? (
        <div style={CARD}>
          <div style={{ padding: "12px 14px", display: "flex", alignItems: "center", gap: 8 }}>
            <Pill style={{ width: 16, height: 16, color: TEAL }} strokeWidth={2} />
            <div style={{ fontWeight: 800, color: NAVY, fontSize: 15 }}>Refills to dispense</div>
            <div style={{ marginLeft: "auto", fontSize: 12, color: MUTED, fontWeight: 600 }}>
              {pendingCount} pending
            </div>
          </div>
          {loading ? (
            <div style={{ padding: 24, textAlign: "center", color: MUTED, fontSize: 13 }}>Loading refills…</div>
          ) : groups.length === 0 ? (
            <div style={{ padding: 24, textAlign: "center", color: MUTED, fontSize: 13 }}>
              {(filterDay || filterMonth !== "all")
                ? "No refills match this filter — try Clear."
                : "No pending refills — set Refills to Yes on a client’s record to track one here."}
            </div>
          ) : (
            groups.map((g) => (
              <div key={g.key || "none"}>
                <div
                  data-testid={`pharmacy-refill-day-${g.key || "none"}`}
                  style={{
                    padding: "7px 14px",
                    background: g.key && g.key < today ? "#fdf2f2" : "#f4f7f5",
                    borderTop: `1px solid ${BORDER}`,
                    fontSize: 11, fontWeight: 800, letterSpacing: ".08em", textTransform: "uppercase",
                    color: g.key && g.key < today ? RED : TEAL,
                  }}
                >
                  {g.key ? dayLabel(g.key, today, tomorrow) : "No refill date"}
                </div>
                {g.rows.map(renderPendingRow)}
              </div>
            ))
          )}
        </div>
      ) : (
        <div style={CARD}>
          <div style={{ padding: "12px 14px", display: "flex", alignItems: "center", gap: 8 }}>
            <Check style={{ width: 16, height: 16, color: GREEN }} strokeWidth={2.5} />
            <div style={{ fontWeight: 800, color: NAVY, fontSize: 15 }}>Dispensed history</div>
            <div style={{ marginLeft: "auto", fontSize: 12, color: MUTED, fontWeight: 600 }}>
              {history.length} record{history.length === 1 ? "" : "s"}
            </div>
          </div>
          {loading ? (
            <div style={{ padding: 24, textAlign: "center", color: MUTED, fontSize: 13 }}>Loading…</div>
          ) : history.length === 0 ? (
            <div style={{ padding: 24, textAlign: "center", color: MUTED, fontSize: 13 }}>
              {(filterDay || filterMonth !== "all")
                ? "No dispensed refills match this filter — try Clear."
                : "Nothing dispensed yet — completed refills stay here permanently."}
            </div>
          ) : (
            history.map(renderHistoryRow)
          )}
        </div>
      )}

      {/* Next-refill prompt — shown right after a dispense is completed.
          Scheduling creates a NEW record; the dispensed one stays in history. */}
      <Dialog open={!!nextRefillFor} onOpenChange={(o) => !o && setNextRefillFor(null)}>
        <DialogContent data-testid="pharmacy-next-refill-dialog" className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Schedule next refill?</DialogTitle>
            <DialogDescription>
              {nextRefillFor?.patient_name} was marked dispensed — that record stays in the history.
              If another refill is needed, pick the date and a new pending refill is created.
            </DialogDescription>
          </DialogHeader>
          <div style={{ display: "flex", justifyContent: "center" }}>
            <Calendar
              mode="single"
              selected={nextDate || undefined}
              onSelect={(d) => d && setNextDate(d)}
              className="p-2"
            />
          </div>
          <div style={{ display: "flex", gap: 8, marginTop: 4 }}>
            <button
              type="button"
              onClick={scheduleNextRefill}
              disabled={!nextDate || savingNext}
              data-testid="pharmacy-next-refill-save"
              style={{
                flex: 1, border: "none", background: GREEN, color: "#fff",
                borderRadius: 10, padding: "10px 12px", fontSize: 13, fontWeight: 700,
                cursor: "pointer", opacity: !nextDate || savingNext ? 0.5 : 1,
              }}
            >
              {savingNext ? "Saving…" : `Schedule ${nextDate ? prettyYMD(toYMD(nextDate)) : ""}`}
            </button>
            <button
              type="button"
              onClick={() => setNextRefillFor(null)}
              data-testid="pharmacy-next-refill-done"
              style={{
                border: `1px solid ${BORDER}`, background: "#fff", color: MUTED,
                borderRadius: 10, padding: "10px 14px", fontSize: 13, fontWeight: 600,
                cursor: "pointer",
              }}
            >
              No more refills
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Cancel confirmation — the record is kept as "cancelled", never deleted */}
      <Dialog open={!!cancelFor} onOpenChange={(o) => !o && setCancelFor(null)}>
        <DialogContent data-testid="pharmacy-cancel-dialog" className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Cancel this refill?</DialogTitle>
            <DialogDescription>
              {cancelFor?.patient_name} ({cancelFor?.medication || "no medication"}) — the refill
              scheduled for {prettyYMD(cancelFor?.refill_date)} will be cancelled and leave the
              pending list. The client record itself is kept.
            </DialogDescription>
          </DialogHeader>
          <div style={{ display: "flex", gap: 8, marginTop: 4 }}>
            <button
              type="button"
              onClick={cancelRefill}
              disabled={cancelBusy}
              data-testid="pharmacy-cancel-confirm"
              style={{
                flex: 1, border: "none", background: RED, color: "#fff",
                borderRadius: 10, padding: "10px 12px", fontSize: 13, fontWeight: 700,
                cursor: "pointer", opacity: cancelBusy ? 0.5 : 1,
              }}
            >
              {cancelBusy ? "Cancelling…" : "Yes, cancel refill"}
            </button>
            <button
              type="button"
              onClick={() => setCancelFor(null)}
              data-testid="pharmacy-cancel-keep"
              style={{
                border: `1px solid ${BORDER}`, background: "#fff", color: MUTED,
                borderRadius: 10, padding: "10px 14px", fontSize: 13, fontWeight: 600,
                cursor: "pointer",
              }}
            >
              Keep it
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
